import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { FaClipboardList, FaXmark } from 'react-icons/fa6';
import displayNPRCurrency from '../helpers/displayCurrency';

const OrderDetailsModal = ({ isOpen, onClose, order }) => {
    if (!isOpen || !order) return null;

    const { 
        shippingAddress, 
        productDetails, 
        paymentDetails, 
        orderStatus, 
        trackingNumber, 
        courierName,
        totalAmount,
        discountAmount,
        shippingCost,
        createdAt,
        _id
    } = order;

    return (
        <AnimatePresence>
            <div className='fixed inset-0 z-[100] flex items-center justify-center px-4'>
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className='absolute inset-0 bg-primary/60 backdrop-blur-sm'
                />
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className='relative bg-white w-full max-w-2xl rounded-[2rem] p-6 md:p-8 shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-none'
                >
                    <button onClick={onClose} className='absolute top-4 right-4 md:top-6 md:right-6 p-2 hover:bg-surface-muted rounded-full transition-all'>
                        <FaXmark className='text-gray-400' />
                    </button>

                    <div className='mb-6 md:mb-8 border-b border-surface-border pb-6'>
                        <div className='flex items-center gap-3 mb-2'>
                            <div className='p-3 bg-accent/10 rounded-xl text-accent'>
                                <FaClipboardList className='text-xl' />
                            </div>
                            <div>
                                <h2 className='text-xl md:text-2xl font-black text-primary'>Order Details</h2>
                                <p className='text-xs md:text-sm text-gray-500 font-medium'>
                                    ID: <span className='font-mono text-primary font-bold'>#{_id?.slice(-8).toUpperCase()}</span> • {moment(createdAt).format('MMMM D, YYYY')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8'>
                        {/* Shipping Info */}
                        <div className='bg-surface-muted/30 p-5 rounded-2xl border border-surface-border'>
                            <h3 className='text-xs font-black uppercase tracking-widest text-gray-400 mb-3'>Shipping Address</h3>
                            <div className='text-sm text-primary font-medium leading-relaxed'>
                                <p className='font-bold'>{order.customerName}</p>
                                <p>{shippingAddress?.addressLine1}</p>
                                {shippingAddress?.addressLine2 && <p>{shippingAddress?.addressLine2}</p>}
                                <p>{shippingAddress?.city}, {shippingAddress?.state} {shippingAddress?.pincode}</p>
                                <p>{shippingAddress?.country}</p>
                                <p className='mt-2 text-gray-500 text-xs'>Phone: {shippingAddress?.phone}</p>
                            </div>
                        </div>

                        {/* Order Status & Tracking */}
                        <div className='bg-surface-muted/30 p-5 rounded-2xl border border-surface-border'>
                            <h3 className='text-xs font-black uppercase tracking-widest text-gray-400 mb-3'>Status & Tracking</h3>
                            <div className='space-y-3'>
                                <div className='flex justify-between items-center'>
                                    <span className='text-sm text-gray-500 font-medium'>Order Status</span>
                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full
                                        ${orderStatus === 'delivered' ? 'bg-green-100 text-green-600' : 
                                          orderStatus === 'cancelled' ? 'bg-red-100 text-red-600' : 
                                          'bg-blue-100 text-blue-600'}`
                                    }>
                                        {orderStatus}
                                    </span>
                                </div>
                                <div className='flex justify-between items-center'>
                                    <span className='text-sm text-gray-500 font-medium'>Payment</span>
                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full
                                        ${paymentDetails?.payment_status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`
                                    }>
                                        {paymentDetails?.payment_status}
                                    </span>
                                </div>
                                {trackingNumber && (
                                    <div className='pt-2 border-t border-surface-border mt-2'>
                                        <p className='text-xs text-gray-500 mb-1'>Tracking Number ({courierName})</p>
                                        <p className='font-mono font-bold text-primary text-sm tracking-wide'>{trackingNumber}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Products List */}
                    <div className='mb-8'>
                        <h3 className='text-xs font-black uppercase tracking-widest text-gray-400 mb-4'>Items Ordered</h3>
                        <div className='space-y-4'>
                            {productDetails.map((product, idx) => (
                                <div key={idx} className='flex items-center gap-4 p-3 bg-white border border-surface-border rounded-xl shadow-sm'>
                                    <div className='w-14 h-14 bg-surface-muted rounded-lg p-1 flex-shrink-0'>
                                        <img src={product.image[0]} alt={product.name} className='w-full h-full object-contain mix-blend-multiply' />
                                    </div>
                                    <div className='flex-1 min-w-0'>
                                        <h4 className='text-sm font-bold text-primary truncate max-w-[200px]'>{product.name}</h4>
                                        <p className='text-xs text-gray-500'>Qty: {product.quantity} × {displayNPRCurrency(product.price)}</p>
                                    </div>
                                    <div className='text-right'>
                                        <p className='text-sm font-black text-primary'>{displayNPRCurrency(product.price * product.quantity)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Cost Breakdown */}
                    <div className='bg-primary/5 p-6 rounded-2xl border border-primary/10 space-y-3'>
                        <div className='flex justify-between items-center text-sm'>
                            <span className='text-gray-500 font-medium'>Subtotal</span>
                            <span className='font-bold text-primary'>{displayNPRCurrency(productDetails.reduce((sum, p) => sum + (p.price * p.quantity), 0))}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className='flex justify-between items-center text-sm text-green-600'>
                                <span className='font-medium'>Discount {order.couponUsed ? `(${order.couponUsed})` : ''}</span>
                                <span className='font-bold'>-{displayNPRCurrency(discountAmount)}</span>
                            </div>
                        )}
                        <div className='flex justify-between items-center text-sm'>
                            <span className='text-gray-500 font-medium'>Shipping</span>
                            <span className='font-bold text-primary'>{shippingCost > 0 ? displayNPRCurrency(shippingCost) : 'Free'}</span>
                        </div>
                        <div className='pt-3 border-t border-primary/10 flex justify-between items-end mt-2'>
                            <span className='text-base font-black text-primary'>Total</span>
                            <span className='text-xl font-black text-primary'>{displayNPRCurrency(totalAmount)}</span>
                        </div>
                    </div>

                    {/* Refund Status if applicable */}
                    {order.refundStatus && order.refundStatus !== 'none' && (
                         <div className='mt-6 p-4 bg-orange-50 border border-orange-100 rounded-xl'>
                            <h4 className='text-sm font-bold text-orange-800 mb-1'>Return/Refund Status</h4>
                            <p className='text-xs text-orange-700 font-medium'>
                                Status: <span className='uppercase font-bold'>{order.refundStatus}</span>
                                {order.refundAmount > 0 && ` • Amount Refunded: ${displayNPRCurrency(order.refundAmount)}`}
                            </p>
                        </div>
                    )}

                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default OrderDetailsModal;
